/**
 * ChatComposer — THE input surface, shared by the product home and every
 * session (round 3: one composer, not two).
 *
 * It is a single rounded field: you type at the top, and along its bottom
 * edge, inside the same border, sit the model/effort pickers on the left and
 * Send on the right. That is the ChatGPT/Claude/Conductor arrangement, and
 * it is why the box lights its border on focus-within: the whole card is the
 * input, not just the text.
 *
 * The focus ring belongs to the CARD, not the textarea: the whole surface
 * is the control, and two nested purple boxes read as a bug.
 *
 * Round 7, owner: "The input text field can be much bigger and wider." So it
 * is: it rests two lines tall instead of one, it grows to ten before it
 * scrolls, and the padding around the text went from a form input's to a
 * writing surface's. Both mounts share every one of those numbers, because
 * they are the same component.
 *
 * Round 7 again, owner: "The input text field should span the whole chat
 * width." The two mounts want DIFFERENT widths, so the card takes one:
 *  - `width="measure"` (the default, and the product home): centred, capped
 *    at the ~768px a modern chat composer uses. The home page is a wide empty
 *    canvas, and a field bled across all of it reads as a text editor, not as
 *    a place to ask one question.
 *  - `width="full"` (in session): the box IS the column, minus the column's
 *    own padding. A ~780px chat column with a 768px cap leaves a few pixels of
 *    dead margin on each side, which is exactly the inset look the owner was
 *    pointing at, and the cap does nothing at all until the column is dragged
 *    past 768.
 *
 * - autosizing textarea, minRows..10 (no fixed height; beyond 10 it scrolls);
 * - Enter sends, Shift+Enter breaks the line, and composing IME input never
 *   sends (nativeEvent.isComposing / keyCode 229 guard);
 * - a compact rounded-square accent Send button (round 9 shrank the 36px
 *   circle: "too big... kinda out of place") that shares its slot with Stop while
 *   a stream is in flight — the textarea itself stays ENABLED during
 *   streaming (type ahead freely);
 * - `controls`: the caller's pickers (ComposerPickers), on the bottom edge.
 *
 * `onSend` may be async and returns whether the text was accepted — a
 * rejected/false send keeps the draft in the box (create failures must not
 * eat what was typed).
 *
 * DECISIONS #95/#96 (owner: "When I select components, they should appear
 * in chat instead", "They should also be able to tag with @ just like how I
 * can tag files in Conductor with @", "The @ should be inline like
 * Conductor"): what the question is about sits IN the text as `@Label`
 * tokens, drawn as pills by a backdrop behind the textarea (the textarea
 * keeps every native behaviour: IME, undo, wrapping, the caret). A part
 * clicked on the drawing arrives as a token too (`insert`), typing `@`
 * opens a picker over the same things, and deleting a token's text is how
 * it leaves. The composer reports the tokens present on every change; what
 * they MEAN is the caller's (ChatPanel).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import {
  insertToken,
  KIND_WORD,
  mentionTokenAt,
  removeToken,
  tokenSpans,
  tokensIn,
  type MentionCandidate,
} from './mentions';

/** What the pane asks the composer to do to its text: tokens in (all of a
 * gesture's parts at once), or tokens out. */
export type ComposerRequest =
  | { kind: 'insert'; candidates: readonly MentionCandidate[] }
  | { kind: 'remove'; labels: readonly string[] };

/** How tall the box may grow before it starts scrolling, in lines. */
const MAX_ROWS = 10;

/**
 * The resting height of the box, in lines. Two, everywhere: a box one line
 * tall reads as a search field, and this is a place to think out loud. The
 * home composer asks for three, because it is the only thing on the page.
 */
export const COMPOSER_MIN_ROWS = 2;
export const HOME_COMPOSER_MIN_ROWS = 3;

/**
 * The centred measure: what the product home's box is capped at, so a field
 * across a wide empty page still reads as one question. The number lives in
 * tokens.css (--measure-home) because the home page derives its whole column
 * from the same value — the greeting and the suggestions line up with the
 * box by construction, not by two numbers agreeing.
 */
export const COMPOSER_MEASURE_WIDTH = 'mx-auto w-full max-w-(--measure-home)';

/**
 * In session: the whole chat column, minus the column's own padding. No cap,
 * because the column already IS the measure, and a cap inside it only ever
 * shows as dead margin down both sides of the box.
 */
export const COMPOSER_FULL_WIDTH = 'w-full';

/** Which of the two widths a mount asks for. */
export type ComposerWidth = 'measure' | 'full';

export function composerWidthClass(width: ComposerWidth): string {
  return width === 'full' ? COMPOSER_FULL_WIDTH : COMPOSER_MEASURE_WIDTH;
}

/**
 * Rows the autosize textarea should claim for `value` (minRows..MAX_ROWS).
 */
export function composerRows(value: string, minRows = COMPOSER_MIN_ROWS): number {
  const lines = value.split('\n').length;
  return Math.min(MAX_ROWS, Math.max(minRows, lines));
}

/**
 * The token span the caret touches: at its end (`edge` 'end', for
 * Backspace) or at its start ('start', for Delete). Offsets come from the
 * spans' own lengths, so they are the textarea's.
 */
export function tokenSpanAt(
  spans: ReadonlyArray<{ text: string; token: MentionCandidate | null }>,
  caret: number,
  edge: 'start' | 'end',
): { start: number; end: number } | null {
  let at = 0;
  for (const span of spans) {
    const start = at;
    const end = at + span.text.length;
    at = end;
    if (span.token === null) continue;
    if (edge === 'end' && caret === end) return { start, end };
    if (edge === 'start' && caret === start) return { start, end };
    if (caret > start && caret < end) return { start, end };
  }
  return null;
}

/** True when the key event is IME composition traffic, never a send. */
export function isComposingEvent(e: KeyboardEvent<HTMLTextAreaElement>): boolean {
  return e.nativeEvent.isComposing || e.keyCode === 229;
}

function SendIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
    </svg>
  );
}

export function ChatComposer({
  placeholder,
  onSend,
  streaming = false,
  onStop,
  disabled = false,
  controls,
  autoFocus = false,
  minRows = COMPOSER_MIN_ROWS,
  width = 'measure',
  draft = null,
  onDraftConsumed,
  request = null,
  onRequestConsumed,
  mentionCandidates,
  onTokensChange,
}: {
  placeholder: string;
  /**
   * returns whether the text was accepted (false/throw keeps the draft);
   * `tokens` are the things named inline, in text order
   */
  onSend: (text: string, tokens: readonly MentionCandidate[]) => boolean | Promise<boolean>;
  streaming?: boolean;
  onStop?: () => void;
  /** disables SENDING only (e.g. while the session is being created) */
  disabled?: boolean;
  /** the pickers on the input's bottom edge (model/effort) */
  controls?: ReactNode;
  autoFocus?: boolean;
  /** the resting height of the box, in lines */
  minRows?: number;
  /** centred and capped (home), or the full width of its column (session) */
  width?: ComposerWidth;
  /** staged machine draft (palette ask-guide): adopted into the input */
  draft?: string | null;
  onDraftConsumed?: () => void;
  /** tokens to put in or take out (a gesture on the drawing); consumed at once */
  request?: ComposerRequest | null;
  onRequestConsumed?: () => void;
  /** the `@` picker's candidates for a query; absent = no picker */
  mentionCandidates?: (query: string) => readonly MentionCandidate[];
  /** the tokens present in the text, whenever they change */
  onTokensChange?: (tokens: readonly MentionCandidate[]) => void;
}) {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  /** Every candidate ever inserted, by label: what a `@Label` in the text means. */
  const knownRef = useRef<Map<string, MentionCandidate>>(new Map());
  const [knownVersion, setKnownVersion] = useState(0);
  const lastTokensRef = useRef<string>('');

  /** Tell the caller which tokens the text holds now (only when that changed). */
  const reportTokens = useCallback(
    (text: string) => {
      if (onTokensChange === undefined) return;
      const tokens = tokensIn(text, knownRef.current);
      const signature = tokens.map((t) => t.key).join('\u0000');
      if (signature === lastTokensRef.current) return;
      lastTokensRef.current = signature;
      onTokensChange(tokens);
    },
    [onTokensChange],
  );

  /** Put `@Label ` in at the caret for each candidate, in order, and
   * remember what each means: one edit, one report, however many parts a
   * gesture added. */
  const putTokens = useCallback(
    (candidates: readonly MentionCandidate[]) => {
      if (candidates.length === 0) return;
      const el = inputRef.current;
      let text = value;
      let caret = el?.selectionStart ?? value.length;
      for (const candidate of candidates) {
        const next = insertToken(text, caret, candidate.label);
        text = next.text;
        caret = next.caret;
        knownRef.current.set(candidate.label, candidate);
      }
      setKnownVersion((n) => n + 1);
      setValue(text);
      reportTokens(text);
      if (el !== null) {
        el.focus();
        window.requestAnimationFrame(() => el.setSelectionRange(caret, caret));
      }
    },
    [value, reportTokens],
  );
  /** The `@` token the caret is in, and the list it opened; null when closed. */
  const [picker, setPicker] = useState<{
    start: number;
    caret: number;
    query: string;
    options: readonly MentionCandidate[];
    highlighted: number;
  } | null>(null);
  /** A picker the reader dismissed with Escape stays shut for that token. */
  const dismissedRef = useRef<string | null>(null);

  const readPicker = useCallback(
    (text: string, caret: number) => {
      if (mentionCandidates === undefined) return;
      const token = mentionTokenAt(text, caret);
      if (token === null) {
        dismissedRef.current = null;
        setPicker(null);
        return;
      }
      const tokenKey = `${token.start}:${token.query}`;
      if (dismissedRef.current === tokenKey) {
        setPicker(null);
        return;
      }
      const options = mentionCandidates(token.query);
      setPicker(
        options.length === 0
          ? null
          : { start: token.start, caret, query: token.query, options, highlighted: 0 },
      );
    },
    [mentionCandidates],
  );

  // The candidates can arrive AFTER the token was typed (the first `@`
  // is what fetches the flows and the map): re-read the token when they do,
  // so the picker opens the moment there is something to list.
  useEffect(() => {
    const el = inputRef.current;
    if (el === null || mentionCandidates === undefined) return;
    const caret = el.selectionStart ?? el.value.length;
    if (picker === null && mentionTokenAt(el.value, caret) === null) return;
    readPicker(el.value, caret);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read per candidate change
  }, [mentionCandidates]);

  const pickMention = useCallback(
    (candidate: MentionCandidate) => {
      if (picker === null) return;
      // The `@query` the reader typed becomes the token itself.
      const before = value.slice(0, picker.start);
      const after = value.slice(picker.caret).replace(/^\s/, '');
      const token = `@${candidate.label} `;
      const next = before + token + after;
      knownRef.current.set(candidate.label, candidate);
      setKnownVersion((n) => n + 1);
      setValue(next);
      setPicker(null);
      dismissedRef.current = null;
      reportTokens(next);
      const el = inputRef.current;
      if (el !== null) {
        el.focus();
        const caret = before.length + token.length;
        window.requestAnimationFrame(() => el.setSelectionRange(caret, caret));
      }
    },
    [picker, value, reportTokens],
  );

  useEffect(() => {
    if (draft === undefined || draft === null) return;
    setValue(draft);
    reportTokens(draft);
    onDraftConsumed?.();
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt per draft change
  }, [draft]);

  // A gesture on the drawing: the parts' tokens go in at the caret, or come
  // out wherever they are.
  useEffect(() => {
    if (request === undefined || request === null) return;
    if (request.kind === 'insert') {
      putTokens(request.candidates);
    } else {
      const next = request.labels.reduce(
        (text, label) => removeToken(text, label, knownRef.current),
        value,
      );
      setValue(next);
      reportTokens(next);
    }
    onRequestConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one request, one edit
  }, [request]);

  const send = () => {
    const text = value.trim();
    if (text.length === 0 || disabled || sending || streaming) return;
    setSending(true);
    const tokens = tokensIn(value, knownRef.current);
    void Promise.resolve()
      .then(() => onSend(text, tokens))
      .then((accepted) => {
        if (accepted) {
          setValue('');
          reportTokens('');
        }
      })
      .catch(() => {
        // the caller surfaces the error; the draft stays put
      })
      .finally(() => {
        setSending(false);
        inputRef.current?.focus();
      });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (picker !== null) {
      // The picker owns the keys that move through it; everything else
      // keeps typing (and re-reads the token on change).
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const count = picker.options.length;
        setPicker({ ...picker, highlighted: (picker.highlighted + step + count) % count });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (isComposingEvent(e)) return;
        e.preventDefault();
        const chosen = picker.options[picker.highlighted];
        if (chosen !== undefined) pickMention(chosen);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        dismissedRef.current = `${picker.start}:${picker.query}`;
        setPicker(null);
        return;
      }
    }
    // A token is one thing (2026-09-06; owner: "deleting should treat the @
    // as one instead of deleting character by character"): Backspace with
    // the caret at its end, or Delete at its start, takes the whole token
    // and the space after it.
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const el = e.currentTarget;
      const caret = el.selectionStart ?? 0;
      if (caret !== (el.selectionEnd ?? caret)) return;
      const span = tokenSpanAt(spans, caret, e.key === 'Backspace' ? 'end' : 'start');
      if (span === null) return;
      e.preventDefault();
      const after = value.slice(span.end).replace(/^ /, '');
      const next = value.slice(0, span.start) + after;
      setValue(next);
      reportTokens(next);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (input !== null) input.setSelectionRange(span.start, span.start);
      });
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (isComposingEvent(e)) return;
    e.preventDefault();
    send();
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setValue(next);
    reportTokens(next);
    readPicker(next, e.target.selectionStart ?? next.length);
  };

  // The backdrop draws the pills under the text, so it must scroll with it.
  const onScroll = () => {
    const el = inputRef.current;
    const back = backdropRef.current;
    if (el !== null && back !== null) back.scrollTop = el.scrollTop;
  };

  const spans = useMemo(
    () => tokenSpans(value, knownRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- knownVersion stands in for the map
    [value, knownVersion],
  );

  return (
    <div
      data-testid="chat-composer"
      data-width={width}
      className={`${composerWidthClass(width)} relative rounded-panel border border-default bg-raised px-3 pt-3 pb-2 shadow-raised transition-colors duration-(--motion-hover) focus-within:border-(--accent) focus-within:ring-1 focus-within:ring-(--accent)`}
    >
      {picker !== null ? (
        // The `@` picker, over the text so the box itself never moves.
        <ul
          role="listbox"
          data-testid="mention-picker"
          aria-label="Name something in your question"
          className="absolute right-3 bottom-full left-3 z-20 m-0 mb-1 max-h-64 list-none overflow-y-auto rounded-card border border-default bg-raised p-1 shadow-raised"
        >
          {picker.options.map((option, index) => (
            <li
              key={option.key}
              role="option"
              data-testid="mention-option"
              data-kind={option.kind}
              aria-selected={index === picker.highlighted}
              onMouseDown={(e) => {
                e.preventDefault(); // keep the textarea's focus
                pickMention(option);
              }}
              onMouseEnter={() => setPicker({ ...picker, highlighted: index })}
              className={`flex cursor-pointer items-baseline gap-2 rounded-card px-2 py-1 text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] ${
                index === picker.highlighted ? 'bg-inset text-primary' : 'text-secondary'
              }`}
            >
              <span className="w-14 shrink-0 text-[length:var(--text-micro-size)] text-tertiary">
                {KIND_WORD[option.kind]}
              </span>
              <span className="min-w-0 truncate text-primary">{option.label}</span>
              {option.hint !== null ? (
                <span className="min-w-0 flex-1 truncate text-[length:var(--text-micro-size)] text-tertiary">
                  {option.hint}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {/* The text, twice: the backdrop draws the tokens as pills in the exact
          place the textarea draws the same characters (same font, padding
          and wrapping), and the textarea on top keeps every native behaviour.
          The backdrop's plain text is invisible; only the pills show. */}
      <div className="relative">
        <div
          ref={backdropRef}
          aria-hidden
          data-testid="composer-backdrop"
          className="lm-composer-input pointer-events-none absolute inset-0 max-h-72 overflow-hidden px-1 py-0.5 text-[length:var(--text-panel-size)] leading-[var(--text-panel-line)] whitespace-pre-wrap break-words text-transparent"
        >
          {spans.map((span, i) =>
            span.token !== null ? (
              <mark
                key={i}
                data-testid="composer-token"
                data-kind={span.token.kind}
                data-label={span.token.label}
                title={span.token.hint ?? undefined}
                className="lm-composer-token"
              >
                {span.text}
              </mark>
            ) : (
              <span key={i}>{span.text}</span>
            ),
          )}
          {/* a trailing newline needs a character to hold the line open */}
          {value.endsWith('\n') ? ' ' : null}
        </div>
        <textarea
          ref={inputRef}
          data-testid="chat-composer-input"
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onScroll={onScroll}
          placeholder={placeholder}
          rows={composerRows(value, minRows)}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the composer is the page's one action; only the home page and a fresh session ask for it
          autoFocus={autoFocus}
          aria-label={placeholder}
          className="lm-composer-input relative block max-h-72 w-full resize-none border-none bg-transparent px-1 py-0.5 text-[length:var(--text-panel-size)] leading-[var(--text-panel-line)] whitespace-pre-wrap break-words text-primary outline-none placeholder:text-tertiary focus-visible:outline-none"
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        {/* No wrap: in a 380px transcript column the pickers, the session
            readouts and a send button must stay ONE row, shrinking their
            labels, or the composer grows a second line every time the column
            narrows. The readouts hold their size and the pickers give way:
            a truncated model name is still readable, half a percentage is
            not. */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5">{controls}</div>
        {streaming && onStop !== undefined ? (
          <button
            type="button"
            data-testid="chat-composer-stop"
            onClick={onStop}
            title="Stop the reply"
            aria-label="Stop the reply"
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-card border border-strong bg-raised text-primary transition-colors duration-(--motion-hover) hover:border-(--accent)"
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            data-testid="chat-composer-send"
            onClick={send}
            disabled={value.trim().length === 0 || disabled || sending}
            title="Send"
            aria-label="Send"
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-card border border-transparent bg-(--accent) text-(--bg-canvas) transition-opacity duration-(--motion-hover) hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <SendIcon />
          </button>
        )}
      </div>
    </div>
  );
}
